/*
 * Where a material row's identity comes from: the spare parts register, or free text.
 *
 * A consumption row is either pointed at the spare parts register or it is free text, and the
 * two are equal kinds of booking rather than a complete one and an incomplete one. The server
 * is explicit about it: a null sparePartId means free text, not "missing". A bolt out of the drawer will never
 * have a register row and still has to be bookable.
 *
 * Pure functions only, so the rules are testable without rendering the dialog.
 */
import messages from "./messages";

export const MaterialSource = {
  Registered: "registered",
  FreeText: "freetext",
};

/** Which kind an existing row is. A new row starts as Registered — see the dialog. */
export const resolveMaterialSource = (material) =>
  (material?.sparePartId ? MaterialSource.Registered : MaterialSource.FreeText);

/*
 * Blank the four fields the register stamps, so the server can stamp them again.
 *
 * The dialog originally argued the "picked part A, edited the description, then picked part B"
 * case away by leaving these fields empty on a new row: with nothing in them the situation never arises. That
 * held for a new row and not for an edited one, where the form is loaded from what the register
 * already stamped. Re-picking then saved part B's id next to part A's number, name and unit —
 * and `partNumber` was not even on screen to be corrected by hand.
 *
 * Quantity is not here. It is the user's answer to "how many", not the register's.
 */
export const clearStampedFields = (form) => ({
  ...form,
  partNumber: "",
  description: "",
  unit: "",
  unitCost: "",
});

/*
 * The body to send.
 *
 * 🛑 The line that matters is sparePartId. Editing a registered row into a free-text one must
 * send null: MaterialUsageService.UpdateAsync assigns `existing.SparePartId =
 * material.SparePartId` outright, so null clears the link and the parts come back onto the
 * shelf. Carrying the old id through — or omitting the field and letting the mapper decide —
 * would leave the row counting against stock while claiming to name nothing.
 *
 * In register mode the text fields are sent empty on purpose. StampFromSparePartAsync fills
 * whatever the caller left blank from the part itself, and keeping that the single copy of the
 * rule is why the dialog does not prefill them: a second copy has no good answer to "picked
 * part A, edited the description, then picked part B".
 *
 * Anything the user did type still wins, in both modes, because the server only fills blanks.
 *
 * No usedByUserId. The server reads the consumer off the token, so sending one was at
 * best redundant and at worst a way to book a colleague's name onto your own consumption.
 */
export const buildMaterialPayload = (form, source, { companyKey }) => {
  const isRegistered = source === MaterialSource.Registered;
  const trimmed = (value) => (value?.trim() ? value.trim() : null);

  return {
    companyKey,
    sparePartId: isRegistered ? (form.sparePartId ?? null) : null,
    partNumber: trimmed(form.partNumber),
    // Empty string rather than null: the server treats blank as "fill from the part", and
    // Description is required once it has done so.
    description: form.description?.trim() ?? "",
    quantity: Number(form.quantity),
    unit: form.unit?.trim() ?? "",
    unitCost: form.unitCost === "" || form.unitCost === null || form.unitCost === undefined
      ? null
      : Number(form.unitCost),
  };
};

/*
 * Mirrors MaterialUsageService.ValidateMaterialFields, plus the one rule that only exists in
 * the form: register mode has to have a part.
 *
 * Description and unit are NOT required in register mode — the server fills them from the part,
 * and demanding them here would make the register useless for the thing it is for.
 */
export const validateMaterialForm = (form, source, intl) => {
  const errors = {};
  const isRegistered = source === MaterialSource.Registered;

  if (isRegistered && !form?.sparePartId) {
    errors.sparePartId = intl.formatMessage(messages.materialsSparePartRequired);
  }

  if (!isRegistered && !form?.description?.trim()) {
    errors.description = intl.formatMessage(messages.materialsDescriptionRequired);
  }

  if (!isRegistered && !form?.unit?.trim()) {
    errors.unit = intl.formatMessage(messages.materialsUnitRequired);
  }

  // Number("") is 0, so the blank has to be caught before the coercion — the same trap the
  // stock movement form hit. An untouched field is not a quantity of nothing.
  const quantity = Number(form?.quantity);
  if (form?.quantity === "" || form?.quantity === null || form?.quantity === undefined
      || Number.isNaN(quantity) || quantity <= 0) {
    errors.quantity = intl.formatMessage(messages.materialsQuantityPositive);
  }

  if (form?.unitCost !== "" && form?.unitCost !== null && form?.unitCost !== undefined) {
    const unitCost = Number(form.unitCost);
    if (Number.isNaN(unitCost) || unitCost < 0) {
      errors.unitCost = intl.formatMessage(messages.materialsUnitCostInvalid);
    }
  }

  return errors;
};
